import type { TimeSeriesSeries } from '../../TimeSeriesChart/types'
import { resolveSeriesColor } from './seriesColors'
import { parseTelemetryTimeMs } from './parseTelemetryTime'

export { SERIES_COLOR_PALETTE } from './seriesColors'
export { parseTelemetryTimeMs, parseTelemetryDate } from './parseTelemetryTime'

export type HistoryChartRow = {
  time: string
  key: string
  label: string
  /**
   * Numeric measure value. May arrive as a scalar or a nested EnrichedMeasure-like
   * object (`{ value, raw, … }`) from telemetry enrichment — both are accepted.
   */
  raw: unknown
  unit?: string
}

function colorForKey(key: string, index: number): string {
  return resolveSeriesColor(key, index)
}

/**
 * Coerce a measure field to a finite number.
 * Handles double-wrapped telemetry measures where `raw`/`value` are objects
 * like `{ raw: 2500, value: 2500, unit, label }`.
 */
export function toNumeric(raw: unknown, depth = 0): number | null {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw === 'boolean') return null
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    return Number.isFinite(n) ? n : null
  }
  if (typeof raw === 'object' && depth < 3) {
    const o = raw as Record<string, unknown>
    // Prefer canonical `value`, then `raw` (nested EnrichedMeasure shape)
    if ('value' in o) {
      const fromValue = toNumeric(o.value, depth + 1)
      if (fromValue !== null) return fromValue
    }
    if ('raw' in o) return toNumeric(o.raw, depth + 1)
  }
  return null
}

function toEpochMs(time: string): number | null {
  return parseTelemetryTimeMs(time)
}

function normalizeUnit(unit: string): string {
  return unit.trim().toLowerCase().replace(/\s+/g, '')
}

export function isPercentUnit(unit: string): boolean {
  const n = normalizeUnit(unit)
  return n === '%' || n === 'percent' || n === 'pct'
}

/** Power units favored for the left axis (kW preferred over W when both appear). */
export function isPowerUnit(unit: string): boolean {
  const n = normalizeUnit(unit)
  return n === 'kw' || n === 'w' || n === 'mw' || n === 'watt' || n === 'watts'
}

function pickPreferredPowerUnit(units: string[]): string | undefined {
  const power = units.filter(isPowerUnit)
  if (power.length === 0) return undefined
  return (
    power.find((u) => normalizeUnit(u) === 'kw') ??
    power.find((u) => normalizeUnit(u) === 'w') ??
    power[0]
  )
}

function pickPercentUnit(units: string[]): string | undefined {
  return units.find(isPercentUnit)
}

/**
 * Dual-axis assignment for smart-web parity:
 * - Left: prefer kW/W (power)
 * - Right: prefer %
 * Extra units share the left axis.
 */
export function assignDualAxisUnits(units: string[]): { left: string; right?: string } {
  const unique = [...new Set(units.map((u) => u.trim()))]
  if (unique.length === 0) return { left: '' }

  const power = pickPreferredPowerUnit(unique)
  const percent = pickPercentUnit(unique)

  if (power && percent) {
    return { left: power, right: percent }
  }
  if (power) {
    const other = unique.find((u) => u !== power)
    return other ? { left: power, right: other } : { left: power }
  }
  if (percent) {
    const other = unique.find((u) => !isPercentUnit(u))
    return other ? { left: other, right: percent } : { left: percent }
  }
  return unique.length > 1
    ? { left: unique[0], right: unique[1] }
    : { left: unique[0] }
}

function unitMatches(a: string, b: string): boolean {
  return normalizeUnit(a) === normalizeUnit(b)
}

/**
 * Pivot flattened HistoryRow-like records into TimeSeriesSeries for the dumb chart.
 *
 * - Groups by `key`
 * - Dual Y-axes: prefer power (kW/W) on the left and % on the right
 * - Non-numeric / null values become `y: null` gaps (connectNulls: false downstream)
 */
export function historyRowsToSeries(rows: HistoryChartRow[]): TimeSeriesSeries[] {
  type Acc = {
    id: string
    label: string
    unit: string
    points: Map<number, number | null>
  }

  const byKey = new Map<string, Acc>()

  for (const row of rows) {
    const x = toEpochMs(row.time)
    if (x == null) continue

    let acc = byKey.get(row.key)
    if (!acc) {
      acc = {
        id: row.key,
        label: row.label || row.key,
        unit: (row.unit ?? '').trim(),
        points: new Map(),
      }
      byKey.set(row.key, acc)
    } else if (!acc.label && row.label) {
      acc.label = row.label
    } else if (!acc.unit && row.unit) {
      acc.unit = row.unit.trim()
    }

    // Last value wins for duplicate timestamps
    acc.points.set(x, toNumeric(row.raw))
  }

  const allUnits = [...byKey.values()].map((a) => a.unit)
  const { left: axisUnit0, right: axisUnit1 } = assignDualAxisUnits(allUnits)

  const series: TimeSeriesSeries[] = []
  let index = 0
  for (const [key, acc] of byKey) {
    const yAxisIndex: 0 | 1 =
      axisUnit1 !== undefined && unitMatches(acc.unit, axisUnit1) ? 1 : 0

    const points = [...acc.points.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([x, y]) => ({ x, y }))

    series.push({
      id: key,
      label: acc.label || key,
      unit: acc.unit || axisUnit0,
      color: colorForKey(key, index),
      points,
      yAxisIndex,
      visible: true,
    })
    index++
  }

  // Stable sort by label for legend consistency
  series.sort((a, b) => a.label.localeCompare(b.label))
  return series
}
